import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import ArticleImage from "../../components/ArticleImage";
import TextLink from "../../components/TextLink";
import halloweenVotingImage from "../../assets/images/halloween-voting-aws.png";

export const HalloweenCostumeVotingAwsDetails = {
  articleTitle: "Serverless costume voting on AWS",
  articleDate: "2026-11-05",
  articleAuthor: "Harrison Cannon",
  articleImage: halloweenVotingImage,
  articleBody: "I ran a serverless costume vote for about sixty party guests on Cognito, DynamoDB, CloudFront, and GitHub OIDC, including the design choices and where AI helped.",
  articleSlug: "halloween-costume-voting-aws"
};

function HalloweenCostumeVotingAws() {
  function ArticleContent() {
    return (
      <div>
        <h2>Background</h2>

        <p>
          Every year I host a Halloween Bash. Guests show up in costume, and for years the &quot;best dressed&quot; vote was paper slips or a group chat. In 2026 I wanted an app. Guests register with a photo, vote for Best Man and Best Woman costume on their phones, and the host closes voting when the night peaks.
        </p>

        <p>
          About sixty guests, a two-to-three hour window, flaky mobile networks, and software engineers in the room who would try to cheat a naive form. That last point shaped most of the production choices.
        </p>

        <ArticleImage
          image={halloweenVotingImage}
          caption="Architecture for the Halloween voting app"
        />

        <Divider className="divider" />

        <h2>Design constraints</h2>

        <p>
          Before writing infrastructure, I pinned down the constraints:
        </p>

        <ul>
          <li>Identity had to survive refresh, incognito, and direct API calls. Client-only tricks were out.</li>
          <li>One vote per category per guest, changeable until the host closes voting, and no self-votes.</li>
          <li>Guests must not see tallies until voting ends. Hosts need live admin views.</li>
          <li>Costume photos stay private. Public S3 URLs were not acceptable.</li>
          <li>Single-night workload. Pay for what you use, then tear it down.</li>
        </ul>

        <p>
          That led to Cognito for identity, DynamoDB for atomic vote and tally updates, API Gateway and Lambda for the API, and CloudFront in front of the static site and the API.
        </p>

        <Divider className="divider" />

        <h2>Architecture overview</h2>

        <p>
          The CDK app spans two regions because CloudFront and Cognito custom domains need certificates in <code>us-east-1</code>. The workload itself runs in <code>eu-west-1</code> to keep latency low for guests in the UK.
        </p>

        <ul>
          <li><code>HalloweenEdgeStack</code> in <code>us-east-1</code> holds ACM certificates for the site and Cognito Hosted UI domain.</li>
          <li><code>HalloweenRegionalStack</code> in <code>eu-west-1</code> holds Cognito, DynamoDB, S3, API Gateway, Lambdas, CloudFront, Route53, and GitHub OIDC deploy roles.</li>
        </ul>

        <p>
          Request flow:
        </p>

        <ol>
          <li>Guests hit the custom domain via Route53 to CloudFront.</li>
          <li>Static Next.js assets come from a private S3 origin with OAC. Photos come from a second private bucket, only through CloudFront signed URLs.</li>
          <li>CloudFront forwards <code>/api/*</code> to API Gateway and injects an origin API key that browsers never see.</li>
          <li>API methods require a Cognito JWT. Admin routes also require membership of the Cognito <code>admin</code> group.</li>
          <li>Each HTTP method maps to its own Lambda on Node.js 24. Shared data helpers live in a Lambda layer.</li>
        </ol>

        <Divider className="divider" />

        <h2>Auth from phone OTP to email and social login</h2>

        <p>
          The first design used phone OTP through Cognito. Phones are harder to throw away than email aliases, which mattered when some guests were developers. UK SMS is expensive for a self-funded, one-night event, so that design did not stick. Cognito Essentials enabled passwordless logins with OTP over email, and I added Google and Apple via Hosted UI as well.
        </p>

        <p>
          Guests arrive in costume on patchy signal. Entering a phone number and waiting for SMS added friction. Social sign-in gave a faster, more streamlined path onto the register page, which mattered because guests spanned a range of ages and backgrounds. Email OTP stayed available for anyone who preferred not to use Google or Apple. The Hosted UI used a Cognito custom domain under the site hostname.
        </p>

        <p>
          Apple Sign In needs the parent of the Hosted UI hostname to resolve, so that parent record has an A record to a dummy address for Apple&apos;s domain check. IdP credentials sit in Secrets Manager, not CDK context or GitHub secrets.
        </p>

        <p>
          Guests still had to finish a registration profile with name, costume, gender, and an optional camera photo before voting. Cognito proved they were signed in. The DynamoDB guest profile proved they finished onboarding.
        </p>

        <Divider className="divider" />

        <h2>CloudFront at the edge</h2>

        <p>
          Two edge patterns mattered more than WAF for this app.
        </p>

        <h3>API key injection</h3>

        <p>
          API Gateway requires an API key and a usage plan at 100 rps with a burst of 200, which is more than enough for sixty phones. The key is not shipped to the browser. CloudFront attaches it as a custom origin header on <code>/api/*</code>, and the origin request policy does not forward a viewer-supplied <code>x-api-key</code>. With Cognito JWT validation, random clients cannot call the REST API just because they found the execute-api URL.
        </p>

        <h3>Signed photo URLs</h3>

        <p>
          Costume portraits live under <code>/photos/*</code> on a private S3 origin protected by a CloudFront key group. Lambdas create short-lived signed URLs after reading the private key from Secrets Manager. A CDK custom resource generates and rotates that key pair so the signing material never lands in GitHub Actions variables.
        </p>

        <p>
          The static site is a Next.js 15 App Router build with <code>output: &quot;export&quot;</code>. A CloudFront Function rewrites extensionless paths to <code>index.html</code> so client-side routes work on S3 without a server.
        </p>

        <Divider className="divider" />

        <h2>DynamoDB single-table votes</h2>

        <p>
          Access patterns were small and known up front, so a single-table design fitted. Same idea as my earlier <TextLink url="/blog/appsync-dynamodb-single-table-design" text="AppSync and DynamoDB single-table post" />:
        </p>

        <table>
          <thead>
            <tr>
              <th>PK</th>
              <th>SK</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>GUEST#&lt;sub&gt;</code></td>
              <td><code>PROFILE</code></td>
            </tr>
            <tr>
              <td><code>VOTE#&lt;voterSub&gt;</code></td>
              <td><code>CATEGORY#best-man|best-woman</code></td>
            </tr>
            <tr>
              <td><code>TALLY#&lt;category&gt;</code></td>
              <td><code>&lt;targetSub&gt;</code></td>
            </tr>
            <tr>
              <td><code>CONFIG</code></td>
              <td><code>VOTING</code></td>
            </tr>
          </tbody>
        </table>

        <p>
          GSIs support listing votes by target and proxy votes for host overrides. Casting or changing a vote uses a DynamoDB transaction that updates the vote item and adjusts tally counters in one write, with conditional checks and retries on conflicts. That stops concurrent vote changes from corrupting totals when many guests submit at once.
        </p>

        <p>
          The vote Lambda enforces these rules, not only the UI:
        </p>

        <ul>
          <li>Voting must be open, using <code>CONFIG / VOTING</code>.</li>
          <li>The voter must have a registered guest profile.</li>
          <li>No self-votes.</li>
          <li>The target must exist and match the category. Male maps to Best Man, female to Best Woman.</li>
        </ul>

        <p>
          When the host closes voting, guests see winners. Admins had a live tallies view throughout the night.
        </p>

        <Divider className="divider" />

        <h2>One Lambda per method</h2>

        <p>
          API Gateway uses Lambda proxy integration. See <TextLink url="/blog/lambda-proxy" text="Lambda Proxy" />. Each route and verb gets its own function, such as <code>me/get.ts</code>, <code>votes/category/put.ts</code>, and <code>admin/voting/put.ts</code>. Shared code in <code>@halloween/shared</code> ships as a Lambda layer so handlers stay thin.
        </p>

        <p>
          That layout adds more CDK than a single fat router. It also keeps IAM grants tight. The photo upload Lambda gets <code>s3:PutObject</code>. Admin delete gets Cognito admin APIs. When something fails mid-party, CloudWatch logs point at one function instead of a shared router.
        </p>

        <Divider className="divider" />

        <h2>Infrastructure as code and delivery</h2>

        <p>
          Everything is AWS CDK in TypeScript. The first deploy still needs an admin principal because the stack creates the GitHub OIDC roles. After that, two repos deploy on their own:
        </p>

        <ul>
          <li><code>halloween-infra</code> runs <code>cdk deploy --all</code> on push to <code>main</code>.</li>
          <li><code>halloween-app</code> builds the static export, syncs to S3, and invalidates CloudFront.</li>
        </ul>

        <p>
          Both pipelines assume IAM roles with GitHub OIDC. There are no long-lived AWS keys in GitHub secrets. Repository Actions variables hold CDK outputs such as bucket name, distribution id, Cognito IDs, and role ARNs. Signing keys and the API Gateway key stay in AWS. Same pattern as my <TextLink url="/blog/oidc-in-pipelines-github-actions" text="OIDC in pipelines" /> post, used here for a short-lived party app.
        </p>

        <Divider className="divider" />

        <h2>Where AI fitted in the build</h2>

        <p>
          I built this with Cursor agents from an empty repo. What worked:
        </p>

        <ul>
          <li>An interview-style design pass forced clear answers on identity, anti-cheat, and admin powers before any CDK existed. That avoided a larger rewrite when phone OTP later became email and social login.</li>
          <li>Once the NodejsFunction and method pattern existed, AI was useful for creating many near-identical integrations.</li>
          <li>Prompts that said guests include software developers who will manipulate the system produced better auth boundaries than &quot;build a voting form.&quot;</li>
          <li>A burst audit for sixty concurrent users correctly said capacity was fine. The real risks were OTP delivery, OAuth redirects on mobile Safari, and photo upload on party Wi-Fi.</li>
        </ul>

        <p>
          AI was weaker on Apple and Google console setup, SES sandbox exit timing, and the Halloween UI. Those needed human judgment, real devices, and a printed QR flyer on the night.
        </p>

        <Divider className="divider" />

        <h2>Production night lessons</h2>

        <ul>
          <li>Fast email OTP and social login mattered more than animations once guests were on mobile networks.</li>
          <li>QR codes on flyers pointing at the production URL beat telling people the domain out loud.</li>
          <li>Host tools mattered. Closing voting, deleting a bad registration, and casting a proxy vote for someone stuck on OAuth were required on the night.</li>
          <li>Destruction policies and a dedicated account kept cost near zero after the party and avoided leaving portrait photos around.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          The app had real users, a hard deadline, and a room of people who might poke the API. It also had to stay operable from a phone while I was hosting. Cognito handled identity. DynamoDB transactions kept tallies honest. CloudFront held the edge controls. CDK and OIDC made deploys repeatable.
        </p>
      </div>
    );
  }

  return <Article
    article={HalloweenCostumeVotingAwsDetails}
    content={ArticleContent}
  />;
}

export default HalloweenCostumeVotingAws;
